import React, { useState } from "react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const LS_KEY = "mizizi_admin_token";

export default function AdminSampler() {
  const [token, setToken] = useState(() => localStorage.getItem(LS_KEY) || "");
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resendingEmail, setResendingEmail] = useState("");
  const [manualEmail, setManualEmail] = useState("");

  const authedFetch = async (path, opts = {}) => {
    const res = await fetch(`${API_BASE}/api${path}`, {
      ...opts,
      headers: {
        "Content-Type": "application/json",
        "X-Admin-Token": token,
        ...(opts.headers || {}),
      },
    });
    if (res.status === 401) {
      toast.error("Invalid admin token");
      throw new Error("unauthorized");
    }
    return res;
  };

  const loadList = async () => {
    if (!token) return toast.error("Enter your admin token first");
    localStorage.setItem(LS_KEY, token);
    setLoading(true);
    try {
      const res = await authedFetch("/admin/sampler-subscribers");
      const data = await res.json();
      setSubs(data.subscribers || []);
      toast.success(`Loaded ${data.count} subscriber${data.count === 1 ? "" : "s"}`);
    } catch (err) {
      logger.error("Load subs failed:", err);
    }
    setLoading(false);
  };

  const resend = async (email) => {
    setResendingEmail(email);
    try {
      const res = await authedFetch("/admin/resend-sampler", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (data.sent) {
        toast.success(`Sent to ${data.email_masked} · id ${data.resend_id?.slice(0, 8)}…`);
      } else {
        toast.error(`Send failed: ${data.error || "unknown"}`);
      }
      // refresh row
      setSubs((prev) => prev.map((s) => s.email === email ? {
        ...s,
        delivered: data.sent,
        last_resend_id: data.resend_id,
        last_send_error: data.error,
      } : s));
    } catch (err) {
      logger.error("Resend failed:", err);
    }
    setResendingEmail("");
  };

  const manualResend = async (e) => {
    e.preventDefault();
    if (!manualEmail) return;
    await resend(manualEmail.trim().toLowerCase());
  };

  return (
    <div className="bg-mizizi-cream min-h-screen">
      <div className="max-w-5xl mx-auto px-6 md:px-10 pt-24 pb-16">
        <div className="overline text-mizizi-terracotta">Internal · Admin</div>
        <h1 className="font-serif font-light text-5xl mt-4 leading-none">Sampler subscribers</h1>
        <p className="mt-4 text-mizizi-charcoal/70 max-w-xl">
          Paste your admin token, load the subscriber list, and resend the sampler to any address.
          Handy while the Resend sender is still on the shared <code className="text-xs">onboarding@resend.dev</code>
          — delivery to external addresses is restricted by Resend until a verified domain is configured.
        </p>

        <div className="mt-8 grid md:grid-cols-[1fr_auto] gap-3 max-w-2xl">
          <input
            type="password"
            placeholder="Admin token"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            data-testid="admin-token"
            className="w-full px-4 py-3 bg-transparent border border-mizizi-gold/50 focus:outline-none focus:border-mizizi-terracotta"
          />
          <button
            type="button"
            onClick={loadList}
            disabled={loading}
            data-testid="admin-load"
            className="pill pill-primary disabled:opacity-60"
          >
            {loading ? "Loading…" : "Load subscribers"}
          </button>
        </div>

        {/* Manual resend by arbitrary email */}
        <form onSubmit={manualResend} className="mt-6 grid md:grid-cols-[1fr_auto] gap-3 max-w-2xl">
          <input
            type="email"
            placeholder="Resend to any subscriber's email"
            value={manualEmail}
            onChange={(e) => setManualEmail(e.target.value)}
            data-testid="admin-manual-email"
            className="w-full px-4 py-3 bg-transparent border border-mizizi-gold/50 focus:outline-none focus:border-mizizi-terracotta"
          />
          <button
            type="submit"
            disabled={!!resendingEmail}
            data-testid="admin-manual-resend"
            className="pill pill-outline disabled:opacity-60"
          >
            Resend
          </button>
        </form>

        {subs.length > 0 && (
          <div className="mt-10 border border-mizizi-charcoal/15 bg-white/50">
            <table className="w-full text-sm" data-testid="admin-subs-table">
              <thead className="bg-mizizi-forest text-mizizi-cream">
                <tr>
                  <th className="text-left px-4 py-3 overline">Email (masked)</th>
                  <th className="text-left px-4 py-3 overline">Name</th>
                  <th className="text-left px-4 py-3 overline">List</th>
                  <th className="text-left px-4 py-3 overline">Delivered</th>
                  <th className="text-left px-4 py-3 overline">Signed up</th>
                  <th className="text-right px-4 py-3 overline">Action</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.email} className="border-t border-mizizi-charcoal/10">
                    <td className="px-4 py-3 font-mono text-xs">{s.email_masked}</td>
                    <td className="px-4 py-3">{s.first_name || "—"}</td>
                    <td className="px-4 py-3">{s.mailing_list}</td>
                    <td className="px-4 py-3">
                      {s.delivered ? (
                        <span className="text-mizizi-gold">✓ Sent</span>
                      ) : s.last_send_error ? (
                        <span className="text-mizizi-terracotta" title={s.last_send_error}>✗ Failed</span>
                      ) : (
                        <span className="text-mizizi-charcoal/50">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-mizizi-charcoal/60">
                      {s.created_at ? new Date(s.created_at).toLocaleString("en-KE", { timeZone: "Africa/Nairobi" }) : ""}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => resend(s.email)}
                        disabled={resendingEmail === s.email}
                        data-testid={`admin-resend-${s.email}`}
                        className="pill pill-outline text-xs px-3 py-1 disabled:opacity-60"
                      >
                        {resendingEmail === s.email ? "Sending…" : "Resend"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {subs.length === 0 && token && (
          <p className="mt-10 italic text-mizizi-charcoal/50">Load the list to see subscribers.</p>
        )}
      </div>
    </div>
  );
}
